"use client";

import Icon from "@/components/icons/Icons";
import { useState, useEffect } from "react";
import { openHelpModal } from "@/redux/features/HelpModal/helpModalSlice";
import { openModal } from "@/redux/features/GoldInvestModal/goldInvestModalSlice";
import { useDispatch } from "react-redux";
import { apiGet } from "@/utils/apiClient";
import { formatIndianNumber } from "@/utils/commonFunctions";

type MetalType = "gold" | "silver";

type GoldPrice = {
  ExpiryDate: string;
  PriceQuotationUnit: string;
  PercentChange: string;
  trend: "positive" | "negative";
  Symbol: string;
  SpotSymbol: string;
  ClosePrice: string;
  DateTime: string;
  SpotPrice: string;
  NetChange: string;
  LastTradedPrice: string;
  OpenPrice: string;
  CommodityName: string;
};

type GoldPriceData = {
  status: number;
  data: GoldPrice;
  goldRate: number;
};

interface MetalPriceCalcProps {
  metalType?: MetalType;
  currentPrice?: number | null;
}

const MetalPriceCalc: React.FC<MetalPriceCalcProps> = ({ metalType = "gold", currentPrice }) => {
  const [amount, setAmount] = useState<number | string>("10000");
  const [grams, setGrams] = useState<number | string>("0000");
  const [goldPricePerGram, setGoldPricePerGram] = useState<number>(0);
  const MAX_GRAMS = 10000;

  const showError = false;
  const dispatch = useDispatch();

  const prices = {
    gold: goldPricePerGram,
    silver: 135.75, // still static for now
  };

  const pricePerGram = prices[metalType];

  useEffect(() => {
    if (!currentPrice) return;
    setGoldPricePerGram(currentPrice);
  },[currentPrice]);

  useEffect(() => {
    if (currentPrice) return;

    async function fetchGoldPrice() {
      try {
        const res: GoldPriceData = await apiGet("data/goldRate");

        if (!res.status) throw new Error("Something went wrong");

        const quotationUnit = res.data.PriceQuotationUnit; // "10 GRMS"
        const closePrice = parseFloat(res.data.ClosePrice); // string → number

        let unitGrams = 1;

        // Extract grams from "10 GRMS"
        if (quotationUnit) {
          const match = quotationUnit.match(/(\d+)/);
          if (match) {
            unitGrams = parseFloat(match[1]); // 10
          }
        }

        // price per gram = total price / gram units
        const pricePerGram = closePrice / unitGrams;

        // Save price per gram
        setGoldPricePerGram(pricePerGram);
      } catch (error) {
        console.error("Something went wrong", error);
      }
    }

    fetchGoldPrice();
  }, []);

  const handleAmountChange = (value: string) => {
    let v = value.replace(/,/g, "");

    if (v === "") {
      setAmount("0");
      setGrams("0");
      return;
    }

    if (amount === "0" && /^\d$/.test(v)) {
      v = v;
    }

    if (/^0\d/.test(v)) {
      v = v.replace(/^0+/, "");
      if (v === "") v = "0";
    }

    let num = parseFloat(v);

    if (isNaN(Number(v))) {
      num = 0;
      setAmount("0");
      setGrams("0");
      return;
    }

    const maxAmount = MAX_GRAMS * pricePerGram;
    if (!isNaN(num) && num > maxAmount) {
      num = maxAmount;
      v = String(num);
    }

    setAmount(v);

    if (!isNaN(num) && pricePerGram > 0) {
      let gms = num / pricePerGram;

      if (gms > MAX_GRAMS) gms = MAX_GRAMS;

      setGrams(gms.toFixed(3));
    }
  };

  const handleGramsChange = (value: string) => {
    if (value === "") {
      setGrams("0");
      setAmount("0");
      return;
    }

    let num = parseFloat(value);

    if (!isNaN(num)) {
      if (num > MAX_GRAMS) num = MAX_GRAMS;

      setGrams(num);

      if (pricePerGram > 0) {
        const amount = num * pricePerGram;
        setAmount(amount);
      }
    }
  };

  useEffect(() => {
    const rawAmount = parseFloat(amount.toString().replace(/,/g, ""));

    if (!isNaN(rawAmount) && pricePerGram > 0) {
      const gramsValue = rawAmount / pricePerGram;

      setGrams(gramsValue.toFixed(3));

      setAmount(rawAmount.toFixed(1));
    }
  }, [metalType, pricePerGram]);

  return (
    <>
      <div className="bg-transparent p-3 xs:p-5 sm:p-10 rounded-4xl md:rounded-8xl border border-white w-11/12 mx-auto xs:w-full">
        <div className="flex justify-between items-center mb-8 bg-[#DCB30D1C] rounded-full py-2 px-2.5 sm:py-3 sm:px-5">
          <div className="text-2xs xs:text-2sm sm:text-base lg:text-xl">
            <span className="text-[#FFFFFFBF] font-extralight">Buy{metalType === "gold" ? "Gold" : "Silver"} @</span> <span className="font-bold text-white">₹{pricePerGram.toFixed(2)}/g</span> <span className="text-[#FFFFFFBF] font-extralight hidden sm:inline">{metalType === "gold" ? "24K (99.9% Purity)" : "999 Fine"}</span>
          </div>

          <div className="shrink-0 text-[#FFFFFFBF] text-2xs sm:text-2sm font-light flex items-center gap-1">
            {/* Live Sign */}
            <div className="border border-x-[#ffffffc5] border-y-transparent p-1 rounded-full">
              <div className="border border-x-[#FFFFFFc5] border-y-transparent p-1 rounded-full">
                <span className="h-2 w-2 block bg-[#3FFF1D] rounded-full animate-pulse" />
              </div>
            </div>
            Live Price
          </div>
        </div>

        <div className="flex flex-col xs:flex-row items-stretch xs:items-center justify-between gap-4">
          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            {/* Amount */}
            <label className="relative bg-transparent p-4 md:p-6 rounded-4xl border border-[#FFFFFF45] w-full xs:max-2-87.5">
              <span className="text-sm sm:text-base font-semibold text-[#FFFFFF80] mb-1 sm:mb-2.5">Amount</span>
              <div className="relative">
                <span className={`absolute left-3 text-2xl sm:text-3xl md:text-4xl top-1/2 -translate-y-1/2 font-semibold pointer-events-none ${showError ? "text-[#ec4d4d] " : "text-[#FFFFFF80] "}`}>₹</span>
                <input type="text" value={formatIndianNumber(amount)} onChange={(e) => handleAmountChange(e.target.value)} className={`pl-10 text-2xl sm:text-3xl md:text-4xl border-y border-dotted bg-transparent border-t-transparent font-semibold w-full transition-all duration-200 ease-in-out ${showError ? "border-[#ec4d4d] text-[#ec4d4d] " : "border-b-[#e4e4e4] hover:border-b-[#b6b5b5] text-white "}`} />
              </div>
            </label>

            <Icon.doubleArrow className="w-10 h-10 md:w-16 md:h-16 rotate-90 md:rotate-0" />

            {/* Grams */}
            <label className="relative bg-transparent p-4 md:p-6 rounded-4xl border border-[#FFFFFF45] w-full xs:max-2-87.5">
              <span className="text-sm sm:text-base font-semibold text-[#FFFFFF80] mb-1 sm:mb-2.5">Grams</span>
              <div className="relative">
                <input type="text" value={grams} onChange={(e) => handleGramsChange(e.target.value)} className={`pr-24 text-2xl sm:text-3xl md:text-4xl border-y border-dotted bg-transparent border-t-transparent font-semibold w-full transition-all duration-200 ease-in-out ${showError ? "border-[#ec4d4d] text-[#ec4d4d] " : "border-b-[#e4e4e4] hover:border-b-[#b6b5b5] text-white "}`} />
                <span className={`absolute right-2 text-2xl sm:text-3xl md:text-4xl top-1/2 -translate-y-1/2 font-semibold pointer-events-none ${showError ? "text-[#ec4d4d] " : "text-[#FFFFFF80] "}`}>Gms</span>
              </div>
            </label>

            {/* Buttons */}
          </div>
          <div className="w-full space-y-2.5 xs:space-y-7 md:space-y-5 xs:max-w-60">
            <button onClick={() => dispatch(openModal({ amount: Number(amount) }))} className="text-2sm sm:text-base w-full bg-[#C89900] text-white font-medium px-6 py-3 rounded-xl shine-button-hover">
              Invest in {metalType === "gold" ? "Gold" : "Silver"}
            </button>

            <button onClick={() => dispatch(openHelpModal())} className="text-2sm sm:text-base transition w-full bg-transparent duration-300 border border-[#C89900] sm:hover:bg-[#C89900] text-white px-6 py-3 rounded-xl">
              Talk to Our Experts
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MetalPriceCalc;
